// ProcessExchangeModal — Phase 6.
//
// Dual-submit confirmation for an exchange request that is already
// queued (exchange_status='requested').
//
//   "Process Now" → POST /skip/jobs/:enc_id/actions/process_exchange
//                  with { replacement_unit_enc_id, notes, process_now: true }.
//                  Spawns the EXC- child job + completes the parent.
//
//   "Add to Enquiries" → no server call. Closes the modal and leaves
//                  the request queued. The job stays on the active-jobs
//                  page for later processing. Phase 6's locked
//                  decision: do not create a separate enquiry record;
//                  the queued sub-flag IS the queue.
//
// "Process Now" requires a replacement unit; "Add to Enquiries" does
// not (it's a no-op dismissal).

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import { useSkipInventory } from '../../hooks/useSkipInventory';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './ProcessActionModal.module.css';

export interface ProcessExchangeModalProps {
  isOpen: boolean;
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  /** Bug 3 — enc_id of the job's chosen skip_stock_sizes row. Drives
   *  the inventory dropdown filter so only matching units (same
   *  size) are offered as the replacement. Null when the job has no
   *  size (legacy data); the picker stays disabled in that case. */
  skipSizeEncId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ProcessExchangeModal({
  isOpen,
  jobEncId,
  jobNumber,
  customerName,
  skipSizeEncId,
  onClose,
  onSuccess,
}: ProcessExchangeModalProps): JSX.Element | null {
  const qc = useQueryClient();
  const inventoryQuery = useSkipInventory(
    skipSizeEncId !== null
      ? { status: 'available', skip_size_enc_id: skipSizeEncId }
      : { status: 'available' },
  );
  const inventory = inventoryQuery.data?.inventory ?? [];

  const [replacementEncId, setReplacementEncId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function processNow() {
    setError(null);
    if (!replacementEncId) {
      setError('Choose a replacement unit before processing now.');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(
        endpoints.skipJobs.action(jobEncId, 'actions/process_exchange'),
        {
          replacement_unit_enc_id: replacementEncId,
          notes: notes.trim() || undefined,
          process_now: true,
        },
      );
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(jobEncId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
      onSuccess?.();
      onClose();
      setReplacementEncId('');
      setNotes('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  /** "Add to Enquiries" — client-only dismissal. Leaves the sub-flag
   *  state unchanged so the job remains on the active-jobs page. */
  function addToEnquiries() {
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title="Process Exchange"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={addToEnquiries}
            disabled={submitting}
            aria-label="Add to Enquiries: leave queued for later processing"
          >
            Add to Enquiries
          </Button>
          <Button
            variant="primary"
            onClick={() => void processNow()}
            loading={submitting}
          >
            Process Now
          </Button>
        </div>
      }
    >
      <p className={styles.body}>
        The existing skip on <strong>{jobNumber}</strong> ({customerName}) will
        be collected and replaced with a new unit.
      </p>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="replacement-unit">
          Replacement unit (required for Process Now)
        </label>
        <select
          id="replacement-unit"
          className={styles.select}
          value={replacementEncId}
          onChange={(e) => setReplacementEncId(e.target.value)}
          disabled={skipSizeEncId === null}
        >
          <option value="">
            {skipSizeEncId === null
              ? 'No size on the original job — set a size to enable replacement'
              : 'Select replacement unit…'}
          </option>
          {inventory.map((u) => (
            <option key={u.enc_id} value={u.enc_id}>
              {u.skip_number}
              {u.stock_type_name ? ` — ${u.stock_type_name}` : ''}
              {u.size_name ? ` ${u.size_name}` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="exchange-notes">
          Notes (optional)
        </label>
        <textarea
          id="exchange-notes"
          className={styles.textarea}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      <p className={styles.note}>
        "Add to Enquiries" leaves the request queued; the job stays on
        the active-jobs page until you Process Now.
      </p>
    </Modal>
  );
}
